/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { CustomerSearchField } from '../../customers/public-api'
import type { CustomerSearchSelection } from '../../customers/public-api'
import { getCustomerAccountById } from '../../customers/public-api';
import { createWorkOrder } from '../infra/workOrdersRepo';
import { SUBJECT_LABELS } from '../domain/workOrderSubject';
import type { WorkOrderDraft, WorkOrderSubjectType } from '../domain/types';
import { listVehicles } from '../../vehicles/infra/vehiclesRepo'
import { vehicleDisplay } from '../../vehicles/domain/types';
import type { VehicleListItem } from '../../vehicles/domain/types';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';

/** Gomería v1: solo vehículo o cliente suelto. */
const SUBJECT_OPTIONS: WorkOrderSubjectType[] = ['vehicle', 'customer'];

export function WorkOrderFormScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const appointmentIdParam: string | null = route.params?.appointmentId ?? null;
  const customerNameParam: string | null = route.params?.customerName ?? null;
  const customerIdParam: string | null = route.params?.customerId ?? null;
  const branchId = useSessionStore((s) => s.currentBranchId);
  const canEdit = useModulePermission('WorkOrders', 'edit');
  const capabilityGate = useCapabilityGate('work_orders', {
    title: 'Órdenes de trabajo no disponibles',
    subtitle: 'Este rubro no tiene órdenes de trabajo.',
  });

  const [customerName, setCustomerName] = useState(customerNameParam ?? '');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchSelection | null>(
    customerIdParam && customerNameParam
      ? { id: customerIdParam, name: customerNameParam, phone: null }
      : null,
  );
  const [subjectType, setSubjectType] = useState<WorkOrderSubjectType>('vehicle');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [subjectPickerOpen, setSubjectPickerOpen] = useState(false);

  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleResults, setVehicleResults] = useState<VehicleListItem[]>([]);
  const [vehicleSearching, setVehicleSearching] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleListItem | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const id = await getSessionOrganizationId();
      setOrgId(id);
    })();
  }, []);

  useEffect(() => {
    if (subjectType !== 'vehicle') return;
    if (!orgId) return;
    if (selectedVehicle) return;
    const query = vehicleSearch.trim();
    if (query.length < 2) {
      setVehicleResults([]);
      return;
    }
    setVehicleSearching(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const results = await listVehicles(orgId, { search: query });
          setVehicleResults(results);
        } catch {
          setVehicleResults([]);
        } finally {
          setVehicleSearching(false);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [vehicleSearch, subjectType, orgId, selectedVehicle]);

  const applyCustomerFromVehicle = useCallback(async (v: VehicleListItem) => {
    if (!v.customerId) return;
    if (selectedCustomer) return;
    try {
      const account = await getCustomerAccountById(v.customerId);
      if (account) {
        setSelectedCustomer({
          id: account.id,
          name: account.name,
          phone: account.phone || null,
        });
        setCustomerName(account.name);
      }
    } catch {
      // ponytail: vehicle may have stale customer_id
    }
  }, [selectedCustomer]);

  const handleSelectVehicle = useCallback(
    (v: VehicleListItem) => {
      setSelectedVehicle(v);
      setVehicleSearch('');
      setVehicleResults([]);
      void applyCustomerFromVehicle(v);
    },
    [applyCustomerFromVehicle],
  );

  const handleClearVehicle = useCallback(() => {
    setSelectedVehicle(null);
  }, []);

  const handleCreateVehicle = useCallback(() => {
    navigation.navigate('VehicleForm', {});
  }, [navigation]);

  const handleSave = useCallback(async () => {
    const name = (selectedCustomer?.name ?? customerName).trim();
    if (!name) {
      showError('Falta el cliente', 'Buscá un cliente o escribí el nombre.');
      return;
    }
    if (!branchId) {
      showError('Error', 'No hay sucursal seleccionada.');
      return;
    }
    setSaving(true);
    try {
      const oid = orgId ?? (await getSessionOrganizationId());
      if (!oid) {
        showError('Error', 'No hay organización en sesión. Volvé a iniciar sesión.');
        setSaving(false);
        return;
      }
      const draft: WorkOrderDraft = {
        branchId,
        subjectType,
        subjectId: selectedVehicle?.id ?? null,
        customerId: selectedCustomer?.id ?? selectedVehicle?.customerId ?? null,
        customerName: name,
        notes: notes.trim(),
        appointmentId: appointmentIdParam,
      };
      await createWorkOrder(oid, draft);
      showSuccess('OT creada', `OT para ${name}`);
      navigation.goBack();
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'work-orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo crear la orden de trabajo.' });
    } finally {
      setSaving(false);
    }
  }, [
    branchId,
    customerName,
    notes,
    subjectType,
    navigation,
    orgId,
    selectedVehicle,
    selectedCustomer,
    appointmentIdParam,
  ]);

  const isVehicleSubject = subjectType === 'vehicle';

  if (!capabilityGate.allowed) {
    return <ShellContentFrame>{capabilityGate.blocked}</ShellContentFrame>;
  }

  // Gate: only users with WorkOrders.edit can create OTs.
  // The list hides the "+ Nueva OT" button, but deep-links from Agenda could bypass it.
  if (!canEdit) {
    return (
      <ShellContentFrame>
        <ModuleEmptyState
          title="Sin permiso"
          subtitle="No tenés permiso para crear órdenes de trabajo."
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame>
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <View style={styles.field}>
          <Text style={styles.label}>Cliente</Text>
          <CustomerSearchField
            selected={selectedCustomer}
            onSelect={(c) => {
              setSelectedCustomer(c);
              setCustomerName(c.name);
            }}
            onClear={() => {
              setSelectedCustomer(null);
              setCustomerName('');
            }}
            freeTextName={customerName}
            onFreeTextNameChange={setCustomerName}
            allowFreeText
            testID="work-order-form.customer-search"
            inputTestID="work-order-form.customer"
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>¿Qué se trabaja?</Text>
          <TouchableOpacity
            style={styles.picker}
            onPress={() => setSubjectPickerOpen((v) => !v)}
            testID="work-order-form.subject-picker"
          >
            <Text style={styles.pickerText}>{SUBJECT_LABELS[subjectType]}</Text>
            <Text style={styles.pickerArrow}>{subjectPickerOpen ? '▲' : '▼'}</Text>
          </TouchableOpacity>
          {subjectPickerOpen ? (
            <View style={styles.pickerOptions}>
              {SUBJECT_OPTIONS.map((opt) => (
                <TouchableOpacity
                  key={opt}
                  style={[styles.pickerOption, opt === subjectType && styles.pickerOptionActive]}
                  onPress={() => {
                    setSubjectType(opt);
                    setSubjectPickerOpen(false);
                    if (opt !== 'vehicle') {
                      setSelectedVehicle(null);
                      setVehicleSearch('');
                      setVehicleResults([]);
                    }
                  }}
                >
                  <Text
                    style={[
                      styles.pickerOptionText,
                      opt === subjectType && styles.pickerOptionTextActive,
                    ]}
                  >
                    {SUBJECT_LABELS[opt]}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
        </View>

        {isVehicleSubject ? (
          // El tipo ya dice "Vehículo" arriba; el placeholder explica el campo.
          // Sin label para no repetir la palabra dos veces seguidas.
          <View style={styles.field}>
            {selectedVehicle ? (
              <View style={styles.selectedVehicleCard} testID="work-order-form.vehicle-selected">
                <View style={styles.selectedVehicleInfo}>
                  <Text style={styles.selectedVehicleText}>{vehicleDisplay(selectedVehicle)}</Text>
                  {selectedVehicle.plate ? (
                    <Text style={styles.selectedVehiclePlate}>{selectedVehicle.plate}</Text>
                  ) : null}
                </View>
                <TouchableOpacity
                  onPress={handleClearVehicle}
                  style={styles.clearVehicleBtn}
                  testID="work-order-form.vehicle-clear"
                >
                  <Text style={styles.clearVehicleText}>Quitar</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View>
                <TextInput
                  style={styles.input}
                  value={vehicleSearch}
                  onChangeText={setVehicleSearch}
                  placeholder="Buscar por patente, marca o modelo…"
                  placeholderTextColor={colors.inputPlaceholder}
                  autoCorrect={false}
                  testID="work-order-form.vehicle-search"
                />
                {vehicleSearching ? (
                  <ActivityIndicator size="small" color={colors.accent} style={styles.searchSpinner} />
                ) : null}
                {vehicleResults.length > 0 ? (
                  <View style={styles.vehicleResults} testID="work-order-form.vehicle-results">
                    {vehicleResults.slice(0, 8).map((v) => (
                      <TouchableOpacity
                        key={v.id}
                        style={styles.vehicleOption}
                        onPress={() => handleSelectVehicle(v)}
                      >
                        <Text style={styles.vehicleOptionText}>{vehicleDisplay(v)}</Text>
                        {v.plate ? (
                          <Text style={styles.vehicleOptionPlate}>{v.plate}</Text>
                        ) : null}
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : null}
                {vehicleSearch.trim().length >= 2 &&
                !vehicleSearching &&
                vehicleResults.length === 0 ? (
                  <View style={styles.noResultsBox}>
                    <Text style={styles.noResults}>No se encontraron vehículos.</Text>
                    <TouchableOpacity
                      style={styles.createVehicleBtn}
                      onPress={handleCreateVehicle}
                      testID="work-order-form.create-vehicle"
                    >
                      <Text style={styles.createVehicleText}>+ Crear vehículo</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
            )}
          </View>
        ) : null}

        <View style={styles.field}>
          <Text style={styles.label}>Notas</Text>
          <TextInput
            style={[styles.input, styles.notesInput]}
            value={notes}
            onChangeText={setNotes}
            placeholder="Notas internas (opcional)"
            placeholderTextColor={colors.inputPlaceholder}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
            testID="work-order-form.notes"
          />
        </View>

        <View style={styles.actions}>
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => navigation.goBack()}
            disabled={saving}
          >
            <Text style={styles.cancelButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={() => void handleSave()}
            disabled={saving}
            testID="work-order-form.save"
          >
            {saving ? (
              <ActivityIndicator color="#fff" size="small" />
            ) : (
              <Text style={styles.saveButtonText}>Guardar</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  content: { padding: spacing.md, paddingBottom: spacing.xxl },
  field: { marginBottom: spacing.md + 2 },
  label: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  input: {
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    fontSize: 15,
    color: colors.textPrimary,
  },
  notesInput: { minHeight: 90 },
  picker: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerText: { fontSize: 15, color: colors.textPrimary },
  pickerArrow: { fontSize: 12, color: colors.textSecondary },
  pickerOptions: {
    marginTop: spacing.xs,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    overflow: 'hidden',
  },
  pickerOption: { paddingVertical: spacing.md, paddingHorizontal: spacing.md },
  pickerOptionActive: { backgroundColor: colors.accentSoft },
  pickerOptionText: { fontSize: 15, color: colors.textSecondary },
  pickerOptionTextActive: { color: colors.accent, fontWeight: '600' },
  selectedVehicleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.accentSoft,
    borderWidth: 1,
    borderColor: colors.accent,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
  },
  selectedVehicleInfo: { flex: 1 },
  selectedVehicleText: { fontSize: 15, color: colors.accentStrong, fontWeight: '600' },
  selectedVehiclePlate: { fontSize: 13, color: colors.accent, marginTop: 2 },
  clearVehicleBtn: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  clearVehicleText: { color: colors.danger, fontSize: 13, fontWeight: '600' },
  searchSpinner: { marginTop: spacing.xs },
  vehicleResults: {
    marginTop: spacing.xs,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    overflow: 'hidden',
  },
  vehicleOption: {
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderSoft,
  },
  vehicleOptionText: { fontSize: 14, color: colors.textPrimary, fontWeight: '500' },
  vehicleOptionPlate: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  noResults: { fontSize: 13, color: colors.textMuted, marginTop: spacing.xs, fontStyle: 'italic' },
  noResultsBox: { marginTop: spacing.xs },
  createVehicleBtn: {
    marginTop: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.accentSoft,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  createVehicleText: { color: colors.accent, fontSize: 14, fontWeight: '600' },
  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.md },
  cancelButton: {
    flex: 1,
    paddingVertical: spacing.md,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: colors.controlSurface,
  },
  cancelButtonText: { color: colors.textSecondary, fontSize: 16, fontWeight: '600' },
  saveButton: {
    flex: 1,
    paddingVertical: spacing.md,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: colors.accent,
  },
  saveButtonDisabled: { opacity: 0.6 },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: '700' },
});
